/* eslint-disable jsdoc/require-jsdoc */
import {
  AngleMode,
  Chamfer,
  Chamfer2SlotParams,
  ChamferSlotParams,
  ClampJoint,
  ClampJointCountSettings,
  Countertop,
  CountertopAngleProcessing,
  CountertopAngleProcessingBase,
  CountertopAngleProcessingResponse,
  CountertopEdgeBanding,
  CountertopEdgeBandingResponse,
  CountertopFastenersSet,
  CountertopFastenersSetResponse,
  CountertopHole,
  CountertopHoleResponse,
  CountertopJointLineTypeRequestEnum,
  CountertopLine,
  CountertopLineResponse,
  CountertopLinesJoint,
  CountertopLinesJointRequest,
  CountertopLinesJointResponse,
  CountertopLinesJointTypeEnum,
  CountertopOutline,
  CountertopOutlineResponse,
  CountertopOutlineSideRequestEnum,
  CountertopOutlineSideResponseEnum,
  CountertopPanelsJoint,
  CountertopRequest,
  CountertopResponse,
  CountertopSlot,
  CountertopSlotResponse,
  CountertopTypeEnum,
  Cut,
  CutInside,
  CutInsideWithAngle,
  FilletSlotParams,
  GShapedCountertop,
  GShapedCountertopLine,
  GShapedCountertopLinesJoints,
  GShapedCountertopPanel,
  GrooveTypeEnum,
  InPlateSlotParams,
  InSideSlotParams,
  PShapedCountertop,
  PShapedCountertopLine,
  PShapedCountertopLinesJoints,
  PShapedCountertopPanel,
  Position,
  PostformingSideEnum,
  RabbetSlotParams,
  Rounding,
  RoundingInside,
  SimpleCountertop,
  SimpleCountertopLine,
  SimpleCountertopPanel,
} from 'cloud-shared-lib';
import _, { isNil } from 'lodash';

export function getFullCountertopThickness(countertop: Countertop): number {
  if (countertop.outline.faceBanding.length) {
    return (
      countertop.material.thickness +
      countertop.outline.faceBanding.map(b => b.material.thickness).reduce((a, v) => a + v)
    );
  } else {
    return countertop.material.thickness;
  }
}

export const getClampJointForCountertop = (
  joints: ClampJoint[],
  thickness: number,
  jointLength: number
): ClampJoint[] => {
  return joints.filter(
    j =>
      thickness >= j.minMaterialThickness &&
      thickness <= j.maxMaterialThickness &&
      getClampJointCountSettings(j, jointLength)
  );
};

export const getClampJointCountSettings = (joint: ClampJoint, jointLength: number): ClampJointCountSettings => {
  return joint.countSettings.find(s => s.maxJointLength >= jointLength && s.minJointLength <= jointLength);
};

const getAvailableJoints = (countertop: Countertop, workJointLength: number, joints: ClampJoint[]): ClampJoint[] => {
  const thickness = getFullCountertopThickness(countertop);
  return getClampJointForCountertop(joints, thickness, workJointLength);
};

export const getAvailableLinesJoints = (
  countertop: Countertop,
  linesJoint: CountertopLinesJoint,
  joints: ClampJoint[]
): ClampJoint[] => {
  const { workJointLength } = getLinesJointLengths(linesJoint, getJointMale(linesJoint, countertop.lines));

  return getAvailableJoints(countertop, workJointLength, joints);
};

export const recalculateLineJoint = (
  countertop: Countertop,
  linesJoint: CountertopLinesJoint,
  joint: ClampJoint
): void => {
  const { jointLength, workJointLength } = getLinesJointLengths(linesJoint, getJointMale(linesJoint, countertop.lines));

  linesJoint.connector = _.cloneDeep(joint);

  if (!linesJoint.connector) {
    return;
  }

  linesJoint.connector.jointLength = jointLength;
  linesJoint.connector.workJointLength = workJointLength;

  const countSettings = getClampJointCountSettings(linesJoint.connector, linesJoint.connector.workJointLength);

  linesJoint.connector.count = countSettings?.count ?? 0;
  linesJoint.connector.interval =
    (linesJoint.connector.workJointLength - 2 * linesJoint.connector.edgeOffset) / (linesJoint.connector.count - 1);
};

export const getAvailablePanelsJoints = (
  countertop: Countertop,
  panelsJoint: CountertopPanelsJoint,
  joints: ClampJoint[]
): ClampJoint[] => {
  const workJointLength = panelsJoint.panel1.width;

  return getAvailableJoints(countertop, workJointLength, joints);
};

export const recalculatePanelJoint = (
  countertop: Countertop,
  panelsJoint: CountertopPanelsJoint,
  joint: ClampJoint
): void => {
  panelsJoint.connector = _.cloneDeep(joint);

  if (!panelsJoint.connector) {
    return;
  }

  panelsJoint.connector.jointLength = panelsJoint.panel1.width;
  panelsJoint.connector.workJointLength = panelsJoint.connector.jointLength;

  const countSettings = getClampJointCountSettings(panelsJoint.connector, panelsJoint.connector.workJointLength);

  panelsJoint.connector.count = countSettings?.count ?? 0;
  panelsJoint.connector.interval =
    (panelsJoint.connector.workJointLength - 2 * panelsJoint.connector.edgeOffset) / (panelsJoint.connector.count - 1);
};

export function mapBusinessObjectsToRequest(countertops: Countertop[]): CountertopRequest[] {
  return countertops.map(c => mapBusinessObjectToRequest(c));
}

export function mapBusinessObjectToRequest(countertop: Countertop): CountertopRequest {
  return {
    ...countertop,
    materials: [countertop.material],
    width: countertop.width,
    length: countertop.length,
    lines: countertop.lines.map(l => ({
      ...l,
      length: l.length,
      width: l.width,
      materialId: l.material.inMaterialBaseId,
      panelsJoints:
        l.panelsJoints?.map(pj => ({
          panel1Uid: pj.panel1.uid,
          panel2Uid: pj.panel2.uid,
          connector: pj.connector,
        })) ?? [],
    })),
    outline: {
      ...countertop.outline,
      slots: _(countertop.outline.slots)
        .map(v => v)
        .flatten()
        .compact()
        .value(),
      angleProcessing: _(countertop.outline.angleProcessing)
        .map(v => v)
        .compact()
        .value(),
      edgeBanding: _(countertop.outline.edgeBanding)
        .map(v => v)
        .compact()
        .value(),
      fastenersSets: countertop.outline.fastenersSets.map(f => ({
        ...f,
        position: position2CountertopSideRequest(f.position),
      })),
      customHoles: countertop.outline.customHoles.map(h => ({
        ...h,
        position: position2CountertopSideRequest(h.position),
      })),
    },
    linesJoints: countertop.linesJoints?.map(
      j =>
        ({
          line1Uid: j.maleUid,
          line2Uid: j.femaleUid,
          line1Type:
            j.type === CountertopLinesJointTypeEnum.MasonsMitre
              ? CountertopJointLineTypeRequestEnum.Male
              : CountertopJointLineTypeRequestEnum.None,
          line2Type:
            j.type === CountertopLinesJointTypeEnum.MasonsMitre
              ? CountertopJointLineTypeRequestEnum.Female
              : CountertopJointLineTypeRequestEnum.None,
          type: j.type,
          length: getJointMale(j, countertop.lines).width,
          parameters: j.params,
          connector: j.connector,
        } as CountertopLinesJointRequest)
    ),
  } as CountertopRequest;
}

export function mapCountertopsToBusinessObject(countertops: CountertopResponse[]): Countertop[] {
  return countertops.map(c => {
    switch (c.type) {
      case CountertopTypeEnum.Simple:
        return mapCountertopResponseToSimpleCountertop(c);

      case CountertopTypeEnum.GShaped:
        return mapCountertopResponseToGShapedCountertop(c);

      case CountertopTypeEnum.PShaped:
        return mapCountertopResponseToPShapedCountertop(c);

      default:
        throw new Error('Unknown countertop type');
    }
  });
}

function mapPanelsJointsResponseToBusinessObject(source: CountertopLineResponse): CountertopPanelsJoint[] {
  return source.panelsJoints.map(pj => {
    const joint = {
      panel1: source.panels.find(p => p.uid === pj.panel1Uid),
      panel2: source.panels.find(p => p.uid === pj.panel2Uid),
      connector: pj.connector ? { ...pj.connector, jointLength: 0, workJointLength: 0, interval: 0 } : undefined,
    };

    if (joint.connector) {
      joint.connector.jointLength = joint.connector.workJointLength = joint.panel1.width;
      joint.connector.interval =
        (joint.connector.workJointLength - joint.connector.edgeOffset * 2) / (joint.connector.count - 1);
    }

    return joint;
  });
}

function mapLinesJointsResponseToBusinessObject<T extends CountertopLinesJoint[]>(
  source: CountertopLinesJointResponse[],
  countertopLines: CountertopLine[],
  create: () => T
): T {
  const joints = create();

  joints.forEach((j, i) => {
    j.type = source[i].type;
    j.params = source[i].parameters;
    j.connector = source[i].connector
      ? {
          ...source[i].connector,
          interval: 0,
          workJointLength: 0,
          jointLength: getJointMale(j, countertopLines).width,
        }
      : undefined;

    if (j.connector) {
      j.connector.workJointLength =
        j.type == CountertopLinesJointTypeEnum.MasonsMitre
          ? j.connector.jointLength - j.params.allowance
          : j.connector.jointLength;

      j.connector.interval = (j.connector.workJointLength - j.connector.edgeOffset * 2) / (j.connector.count - 1);
    }

    return j;
  });

  return joints;
}

function mapCountertopResponseToSimpleCountertop(apiData: CountertopResponse): SimpleCountertop {
  const lines = apiData.lines.map(l => {
    const line = new SimpleCountertopLine(
      l.length,
      l.width,
      { ...apiData.materials[0], postforming: PostformingSideEnum.None },
      {
        panels: l.panels.map(p => {
          const panel = new SimpleCountertopPanel(p.length, p.width);
          panel.uid = p.uid;

          return panel;
        }),
      }
    );

    line.panelsJoints = mapPanelsJointsResponseToBusinessObject(l);

    return line;
  });

  const simpleCountertop = new SimpleCountertop(
    apiData.length,
    apiData.width,
    { ...apiData.materials[0], postforming: PostformingSideEnum.None },
    { lines }
  );

  simpleCountertop.id = apiData.id;
  simpleCountertop.has3d = apiData.has3d;
  simpleCountertop.useClampJointForPanels = apiData.useClampJointForPanels;

  mapOutlineDataToBusinessObject(simpleCountertop.outline, apiData.outline);

  return simpleCountertop;
}

function mapCountertopResponseToGShapedCountertop(apiData: CountertopResponse): GShapedCountertop {
  const lines = apiData.lines.map(l => {
    const line = new GShapedCountertopLine(
      l.length,
      l.width,
      { ...apiData.materials[0], postforming: PostformingSideEnum.None },
      {
        panels: l.panels.map(p => {
          const panel = new GShapedCountertopPanel(p.length, p.width);
          panel.uid = p.uid;

          return panel;
        }),
      }
    );

    line.panelsJoints = mapPanelsJointsResponseToBusinessObject(l);

    return line;
  });

  const gShapedCountertop = new GShapedCountertop(
    apiData.length,
    apiData.width,
    { ...apiData.materials[0], postforming: PostformingSideEnum.None },
    apiData.subtype,
    {
      lines,
    }
  );

  gShapedCountertop.id = apiData.id;
  gShapedCountertop.has3d = apiData.has3d;
  gShapedCountertop.useClampJointForPanels = apiData.useClampJointForPanels;

  gShapedCountertop.linesJoints = mapLinesJointsResponseToBusinessObject(
    apiData.linesJoints,
    gShapedCountertop.lines,
    () =>
      new GShapedCountertopLinesJoints(
        gShapedCountertop.topLine.uid,
        gShapedCountertop.sideLine.uid,
        gShapedCountertop.subtype
      )
  );

  mapOutlineDataToBusinessObject(gShapedCountertop.outline, apiData.outline);

  return gShapedCountertop;
}

function mapCountertopResponseToPShapedCountertop(apiData: CountertopResponse): PShapedCountertop {
  const lines = apiData.lines.map((l, i) => {
    const line = new PShapedCountertopLine(
      l.length,
      l.width,
      { ...apiData.materials[0], postforming: PostformingSideEnum.None },
      {
        panels: l.panels.map(p => {
          const panel = new PShapedCountertopPanel(p.length, p.width);
          panel.uid = p.uid;

          return panel;
        }),
      }
    );

    line.panelsJoints = mapPanelsJointsResponseToBusinessObject(l);

    return line;
  });

  const pShapedCountertop = new PShapedCountertop(
    apiData.length,
    apiData.width,
    { ...apiData.materials[0], postforming: PostformingSideEnum.None },
    apiData.subtype,
    {
      lines,
    }
  );

  pShapedCountertop.id = apiData.id;
  pShapedCountertop.has3d = apiData.has3d;
  pShapedCountertop.useClampJointForPanels = apiData.useClampJointForPanels;

  pShapedCountertop.linesJoints = mapLinesJointsResponseToBusinessObject(
    apiData.linesJoints,
    pShapedCountertop.lines,
    () =>
      new PShapedCountertopLinesJoints(
        pShapedCountertop.topLine.uid,
        pShapedCountertop.leftLine.uid,
        pShapedCountertop.rightLine.uid,
        pShapedCountertop.subtype,
        pShapedCountertop
      )
  );

  mapOutlineDataToBusinessObject(pShapedCountertop.outline, apiData.outline);

  return pShapedCountertop;
}

function mapOutlineDataToBusinessObject(outline: CountertopOutline, apiData: CountertopOutlineResponse): void {
  // Map outline data
  outline.customHoles = apiData.customHoles.map(mapCustomHoleResponseToBusinessObject);
  outline.fastenersSets = apiData.fastenersSets.map(mapFastenersSetResponseToBusinessObject);
  outline.cutouts = apiData.cutouts;
  outline.faceBanding = apiData.faceBanding;

  // Map angle processing data
  for (const angleProcessing of apiData.angleProcessing) {
    outline.angleProcessing[angleProcessing.angleNumber] = mapAngleProcessingResponseToBusinessObject(angleProcessing);
  }

  // Map edge banding data
  for (const edgeBanding of apiData.edgeBanding) {
    outline.edgeBanding[edgeBanding.sideNumber] = mapEdgeBandingResponseToBusinessObject(edgeBanding);
  }

  // Map slots data
  for (const slot of apiData.slots) {
    outline.slots[slot.sideNumber].push(mapSlotResponseToBusinessObject(slot));
  }
}
function mapCustomHoleResponseToBusinessObject(holeData: CountertopHoleResponse): CountertopHole {
  const customHole: CountertopHole = {
    ...holeData,
    position: countertopSideResponse2Position(holeData.position),
  };

  return customHole;
}

function mapFastenersSetResponseToBusinessObject(setData: CountertopFastenersSetResponse): CountertopFastenersSet {
  const fastenersSet: CountertopFastenersSet = {
    ...setData,
    position: countertopSideResponse2Position(setData.position),
  };

  return fastenersSet;
}

function position2CountertopSideRequest(pos: Position): CountertopOutlineSideRequestEnum {
  const map = {
    tc: CountertopOutlineSideRequestEnum.Top,
    bc: CountertopOutlineSideRequestEnum.Bottom,
    rc: CountertopOutlineSideRequestEnum.Right,
    lc: CountertopOutlineSideRequestEnum.Left,
  } as const;

  return map[pos];
}

function countertopSideResponse2Position(pos: CountertopOutlineSideResponseEnum): Position {
  const map = {
    [CountertopOutlineSideResponseEnum.Top]: 'tc',
    [CountertopOutlineSideResponseEnum.Bottom]: 'bc',
    [CountertopOutlineSideResponseEnum.Right]: 'rc',
    [CountertopOutlineSideResponseEnum.Left]: 'lc',
  } as const;

  return map[pos];
}

function mapAngleProcessingResponseToBusinessObject(
  angleProcessingData: CountertopAngleProcessingResponse
): CountertopAngleProcessingBase {
  if (_.isNil(angleProcessingData)) {
    return null;
  }

  const angleProcessing: CountertopAngleProcessing = new CountertopAngleProcessing();

  angleProcessing.mode = angleProcessingData.mode;
  angleProcessing.angleNumber = angleProcessingData.angleNumber;
  angleProcessing.parameters = mapAngleProcessingParamsResponseToBusinessObject(angleProcessingData);

  return angleProcessing;
}

/**
 *
 * @param paramsData
 * @param processing
 */
function mapAngleProcessingParamsResponseToBusinessObject(
  processing: CountertopAngleProcessingResponse
): Rounding | RoundingInside | Cut | CutInside | Chamfer {
  switch (processing.mode) {
    case AngleMode.Chamfer: {
      const params = new Chamfer();
      params.length = processing.parameters.length;
      params.edgeBanding = processing.parameters.edgeBanding;

      return params;
    }

    case AngleMode.Cutted: {
      const params = new Cut();
      params.x = processing.parameters.x;
      params.y = processing.parameters.y;
      params.edgeBanding = processing.parameters.edgeBanding;

      return params;
    }

    case AngleMode.CuttedInside: {
      const params = new CutInside();
      params.r = processing.parameters.r;
      params.x = processing.parameters.x;
      params.y = processing.parameters.y;
      params.edgeBanding = processing.parameters.edgeBanding;

      return params;
    }

    case AngleMode.CuttedInsideWithAngle: {
      const params = new CutInsideWithAngle();
      params.r = processing.parameters.r;
      params.x = processing.parameters.x;
      params.y = processing.parameters.y;
      params.x1 = processing.parameters.x1;
      params.y1 = processing.parameters.y1;
      params.xA = processing.parameters.xA;
      params.yA = processing.parameters.yA;
      params.edgeBanding = processing.parameters.edgeBanding;

      return params;
    }

    case AngleMode.Rounded: {
      const params = new Rounding();
      params.r = processing.parameters.r;
      params.edgeBanding = processing.parameters.edgeBanding;

      return params;
    }

    case AngleMode.RoundedInside: {
      const params = new RoundingInside();
      params.r = processing.parameters.r;
      params.edgeBanding = processing.parameters.edgeBanding;

      return params;
    }
  }
}

function mapEdgeBandingResponseToBusinessObject(edgeBandingData: CountertopEdgeBandingResponse): CountertopEdgeBanding {
  const edgeBanding: CountertopEdgeBanding = {
    ...edgeBandingData,
  };

  return edgeBanding;
}

function mapSlotResponseToBusinessObject(slotData: CountertopSlotResponse): CountertopSlot {
  const slot: CountertopSlot = new CountertopSlot();

  slot.custom = slotData.custom;
  slot.designation = slotData.designation;
  slot.id = slotData.id;
  slot.name = slotData.name;
  slot.plate = slotData.plate;
  slot.type = slotData.type;
  slot.sideNumber = slotData.sideNumber;
  slot.parameters = mapSlotParamsResponseToBusinessObject(slotData);

  return slot;
}

function mapSlotParamsResponseToBusinessObject(
  slotData: CountertopSlotResponse
): FilletSlotParams | ChamferSlotParams | Chamfer2SlotParams | RabbetSlotParams | InPlateSlotParams | InSideSlotParams {
  switch (slotData.type) {
    case GrooveTypeEnum.Chamfer: {
      const params = new ChamferSlotParams();
      params.fi = slotData.parameters.fi;

      return params;
    }

    case GrooveTypeEnum.Chamfer2: {
      const params = new Chamfer2SlotParams();
      params.b = slotData.parameters.b;
      params.dx = slotData.parameters.dx;

      return params;
    }

    case GrooveTypeEnum.Fillet: {
      const params = new FilletSlotParams();
      params.r = slotData.parameters.r;

      return params;
    }

    case GrooveTypeEnum.InPlate: {
      const params = new InPlateSlotParams();
      params.dx = slotData.parameters.dx;
      params.g = slotData.parameters.g;
      params.h = slotData.parameters.h;

      return params;
    }

    case GrooveTypeEnum.InSide: {
      const params = new InSideSlotParams();
      params.dx = slotData.parameters.dx;
      params.g = slotData.parameters.g;
      params.h = slotData.parameters.h;

      return params;
    }

    case GrooveTypeEnum.Rabbet: {
      const params = new RabbetSlotParams();
      params.g = slotData.parameters.g;
      params.h = slotData.parameters.h;

      return params;
    }
  }
}

function getLinesJointLengths(
  joint: CountertopLinesJoint,
  jointMale: CountertopLine
): { jointLength: number; workJointLength: number } {
  const jointLength = jointMale.width;
  const workJointLength =
    joint.type === CountertopLinesJointTypeEnum.MasonsMitre ? jointLength - joint.params.allowance : jointLength;

  return { jointLength, workJointLength };
}

const getJointMale = (joint: CountertopLinesJoint, lines: CountertopLine[]): CountertopLine => {
  const jointMale = lines.find(l => l.uid === joint.maleUid);

  if (isNil(jointMale)) {
    throw new Error(`Joint male line with ID ${joint.maleUid} not found`);
  }

  return jointMale;
};
